import { Autocomplete, TextField } from '@mui/material';
import React, { useCallback } from 'react';
import { SortableChips } from '../../../../../../components/SortableChipsComponents/SortableChips';
import { SIZES } from '../../../../../../constants/constants';
import { FORM_WIDGET_COMPONENTS } from '../../../../../../form/formConstants';
import FormItem from '../../../../../../form/FormItem';
import FormWidget from '../../../../../../form/FormWidget';
import type { GridSettingsSortField } from '../../../types';
import { optionsForIds, toOptionIds, toSortableChipTagProps } from '../../utils';

type ValueOrderFieldProps = {
    sortField: GridSettingsSortField;
    value: string[];
    onChange: (fieldId: string, values: string[]) => void;
};

export function ValueOrderField({ sortField, value, onChange }: ValueOrderFieldProps) {
    const options = sortField.valueOptions ?? [];

    const handleStaticChange = useCallback(
        (next: Array<{ id?: string; label: string }>) => {
            const nextIds = next.map((option) => option.id || option.label);
            onChange(sortField.id, nextIds);
        },
        [sortField.id, onChange]
    );

    const handleMetadataChange = useCallback(
        (_name: string, selectedValue: unknown) => {
            const nextIds = toOptionIds(selectedValue);
            onChange(sortField.id, nextIds);
        },
        [sortField.id, onChange]
    );

    if (sortField.metadataName) {
        return (
            <FormItem>
                <FormWidget
                    name={`valueOrder.${sortField.id}`}
                    label={sortField.rankOrderLabel}
                    component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                    metadataName={sortField.metadataName}
                    {...(sortField.metadataSortBy !== undefined && {
                        sortBy: sortField.metadataSortBy,
                        optionsSort: false
                    })}
                    value={value}
                    multiple
                    draggableChips
                    filterSelectedOptions
                    onChange={handleMetadataChange}
                />
            </FormItem>
        );
    }

    return (
        <FormItem>
            <Autocomplete
                multiple
                fullWidth
                size={SIZES.small}
                options={options}
                value={optionsForIds(value, options)}
                filterSelectedOptions
                disableCloseOnSelect
                isOptionEqualToValue={(option, selected) => option.id === selected.id}
                getOptionLabel={(option) => option.label}
                onChange={(_event, next) => handleStaticChange(next)}
                renderValue={(selected, getItemProps) => (
                    <SortableChips
                        chips={selected}
                        getTagProps={toSortableChipTagProps(getItemProps)}
                        fixedOptions={[]}
                        onReorder={handleStaticChange}
                        getChipKey={(chip, index) => chip.id || `chip-${index}`}
                    />
                )}
                renderInput={(params) => (
                    <TextField {...params} variant="outlined" size={SIZES.small} label={sortField.rankOrderLabel} />
                )}
            />
        </FormItem>
    );
}
